/**
 * Routing.
 *
 * Real paths, not hash fragments, because this surface must be indexable: a
 * crawler has to be able to request `/items/abc` and receive that item's HTML.
 * The same parser runs on the server (to decide what to fetch and render) and
 * in the browser (to decide what to show after a client-side navigation), so
 * the two can never disagree about what a URL means.
 *
 * No customer information is ever in a URL. The catalog carries a search term
 * and filters, an item page carries an item id, and checkout carries a quote or
 * order id. An email address, a name, or an address in a query string would be
 * PII in browser history, in the server access log, and in any referrer — so
 * there is no route here that accepts one.
 */

export type Route =
  | { readonly name: "catalog"; readonly search: string; readonly platform: string; readonly region: string; readonly offset: number }
  | { readonly name: "product"; readonly productID: string }
  | { readonly name: "item"; readonly itemID: string }
  | { readonly name: "cart" }
  | { readonly name: "checkout" }
  | { readonly name: "checkout-return"; readonly orderID: string }
  | { readonly name: "account" }
  | { readonly name: "help"; readonly orderID?: string }
  | { readonly name: "policies" }
  | { readonly name: "not-found"; readonly path: string };

const MAX_SEARCH = 100;
const MAX_ID = 100;
const MAX_OFFSET = 500;

function boundedParam(value: string | null, maximum: number): string {
  if (value === null) return "";
  const trimmed = value.trim();
  if (trimmed.length === 0 || trimmed.length > maximum) return "";
  // Core rejects control characters; refusing them here keeps a hostile query
  // string out of the request it would otherwise be forwarded into.
  return /[\u0000-\u001f\u007f]/.test(trimmed) ? "" : trimmed;
}

function segmentID(raw: string | undefined): string | null {
  if (raw === undefined) return null;
  let decoded: string;
  try {
    decoded = decodeURIComponent(raw);
  } catch {
    return null;
  }
  if (decoded.length === 0 || decoded.length > MAX_ID) return null;
  return /^[A-Za-z0-9._~-]+$/.test(decoded) ? decoded : null;
}

export function parseRoute(pathname: string, search: string): Route {
  const params = new URLSearchParams(search);
  const segments = pathname.split("/").filter((segment) => segment.length > 0);

  if (segments.length === 0 || (segments.length === 1 && segments[0] === "catalog")) {
    const rawOffset = Number(params.get("offset") ?? "0");
    const offset =
      Number.isSafeInteger(rawOffset) && rawOffset >= 0 && rawOffset <= MAX_OFFSET ? rawOffset : 0;
    return {
      name: "catalog",
      search: boundedParam(params.get("q"), MAX_SEARCH),
      platform: boundedParam(params.get("platform"), 80),
      region: boundedParam(params.get("region"), 40),
      offset
    };
  }

  if (segments.length === 2 && segments[0] === "products") {
    const productID = segmentID(segments[1]);
    return productID === null ? { name: "not-found", path: pathname } : { name: "product", productID };
  }

  if (segments.length === 2 && segments[0] === "items") {
    const itemID = segmentID(segments[1]);
    return itemID === null ? { name: "not-found", path: pathname } : { name: "item", itemID };
  }

  if (segments.length === 1 && segments[0] === "cart") return { name: "cart" };
  if (segments.length === 1 && segments[0] === "checkout") return { name: "checkout" };
  if (segments.length === 1 && segments[0] === "account") return { name: "account" };
  if (segments.length === 1 && segments[0] === "help") {
    const orderID = segmentID(params.get("order") ?? undefined);
    return orderID === null ? { name: "help" } : { name: "help", orderID };
  }
  if (segments.length === 1 && segments[0] === "policies") return { name: "policies" };

  if (segments.length === 2 && segments[0] === "checkout" && segments[1] === "return") {
    const orderID = segmentID(params.get("order") ?? undefined);
    return orderID === null
      ? { name: "not-found", path: pathname }
      : { name: "checkout-return", orderID };
  }

  return { name: "not-found", path: pathname };
}

export function catalogHref(query: {
  readonly search?: string;
  readonly platform?: string;
  readonly region?: string;
  readonly offset?: number;
}): string {
  const params = new URLSearchParams();
  if (query.search !== undefined && query.search.length > 0) params.set("q", query.search);
  if (query.platform !== undefined && query.platform.length > 0) params.set("platform", query.platform);
  if (query.region !== undefined && query.region.length > 0) params.set("region", query.region);
  if (query.offset !== undefined && query.offset > 0) params.set("offset", String(query.offset));
  const rendered = params.toString();
  return rendered.length === 0 ? "/catalog" : `/catalog?${rendered}`;
}

export function itemHref(itemID: string): string {
  return `/items/${encodeURIComponent(itemID)}`;
}

export function productHref(productID: string): string {
  return `/products/${encodeURIComponent(productID)}`;
}

export function checkoutReturnHref(orderID: string): string {
  return `/checkout/return?order=${encodeURIComponent(orderID)}`;
}

/** Only a public order reference; never a continuation capability. */
export function orderHelpHref(orderID: string): string {
  return `/help?order=${encodeURIComponent(orderID)}`;
}

/** A human title for the document and for the announced route change. */
export function routeTitle(route: Route, projectName: string): string {
  switch (route.name) {
    case "catalog":
      return route.search.length > 0
        ? `Search: ${route.search} — ${projectName}`
        : `Graded games catalog — ${projectName}`;
    case "product":
      return `Release — ${projectName}`;
    case "item":
      return `Item — ${projectName}`;
    case "cart":
      return `Your cart — ${projectName}`;
    case "checkout":
      return `Checkout — ${projectName}`;
    case "checkout-return":
      return `Confirming your order — ${projectName}`;
    case "account":
      return `Your account — ${projectName}`;
    case "help":
      return `Ask about an item — ${projectName}`;
    case "policies":
      return `Shipping, returns, and terms — ${projectName}`;
    case "not-found":
      return `Page not found — ${projectName}`;
  }
}
